<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="pb-1">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-icon color="primary">mdi-shape-outline</v-icon>
          <span class="text-h6 font-weight-medium">Líneas de negocio</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" small @click="dialogNueva = true">
            <v-icon left small>mdi-plus</v-icon>
            Nueva línea
          </v-btn>
        </div>
        <div class="caption grey--text mt-1 ml-8">
          Cada línea tiene su cuenta de ventas, de mercaderías y de costo de lo
          vendido. Los productos toman la línea de su categoría.
        </div>
      </v-col>

      <v-col v-if="loading" cols="12">
        <v-skeleton-loader v-for="i in 4" :key="i" type="list-item" class="mb-1" />
      </v-col>

      <template v-else>
        <!-- Líneas -->
        <v-col cols="12" class="py-1">
          <v-sheet
            v-if="!lineas.length"
            rounded="lg"
            class="pa-6 text-center"
            color="rgba(255,255,255,0.03)"
          >
            <div class="subtitle-2">Todavía no hay líneas</div>
            <div class="caption grey--text">
              Sin líneas, todas las ventas van a una sola cuenta.
            </div>
          </v-sheet>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Línea</th>
                <th>Ventas</th>
                <th>Mercaderías</th>
                <th>Costo de lo vendido</th>
                <th class="text-right">Productos</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="linea in lineas" :key="linea.id">
                <td>
                  <span class="font-weight-medium">{{ linea.nombre }}</span>
                </td>
                <td class="caption">{{ linea.cuenta_ventas }}</td>
                <td class="caption">{{ linea.cuenta_mercaderias || "—" }}</td>
                <td class="caption">{{ linea.cuenta_cmv || "—" }}</td>
                <td class="text-right">{{ productosPorLinea[linea.id] || 0 }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>

        <!-- Categorías -->
        <v-col cols="12" class="pt-4 pb-1">
          <div class="subtitle-2">Categorías</div>
          <div class="caption grey--text">
            Una subcategoría puede ir a otra línea que su categoría: gana la más
            específica.
            <span v-if="sinLinea"
              >{{ sinLinea }} productos quedan sin línea.</span
            >
          </div>
        </v-col>
        <v-col cols="12" class="pt-1">
          <v-simple-table dense>
            <tbody>
              <tr v-for="categoria in categoriasVisibles" :key="categoria.id">
                <td :style="{ paddingLeft: 16 + (categoria.nivel - nivelBase) * 24 + 'px' }">
                  <span :class="categoria.nivel === nivelBase ? 'font-weight-medium' : 'grey--text'">
                    {{ categoria.nombre }}
                  </span>
                  <span class="caption grey--text ml-1">({{ categoria.productos }})</span>
                </td>
                <td style="width: 280px;">
                  <v-select
                    :value="categoria.linea"
                    :items="opcionesLinea(categoria)"
                    item-text="nombre"
                    item-value="id"
                    dense
                    hide-details
                    :loading="guardando === categoria.id"
                    :disabled="guardando !== null"
                    @change="asignar(categoria, $event)"
                  ></v-select>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </template>
    </v-row>

    <v-dialog v-model="dialogNueva" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">Nueva línea</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="nombreNueva"
            label="Nombre"
            outlined
            dense
            autofocus
            hide-details="auto"
            @keyup.enter="crear"
          ></v-text-field>
          <div class="caption grey--text mt-2">
            Se crean sus subcuentas de ventas, mercaderías y costo. Después
            asignale categorías.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogNueva = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="creando" :disabled="!nombreNueva.trim()" @click="crear">
            Crear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ContabilidadApi from "@/api/contabilidad";
import { getApiErrorMessage } from "@/util/apiError";

const SIN_LINEA = { id: null, nombre: "Sin línea" };

export default {
  name: "EmpresaContabilidadLineas",
  data() {
    return {
      loading: false,
      lineas: [],
      categorias: [],
      guardando: null,
      dialogNueva: false,
      nombreNueva: "",
      creando: false,
    };
  },
  computed: {
    // Con una raíz única ("CATEGORIA") se arranca desde sus hijas
    nivelBase() {
      const raices = this.categorias.filter((c) => c.nivel === 0);
      return raices.length === 1 ? 1 : 0;
    },
    categoriasVisibles() {
      return this.categorias.filter((c) => c.nivel >= this.nivelBase && c.nivel <= this.nivelBase + 1);
    },
    // Línea efectiva de cada categoría: la propia o la del padre
    lineaEfectiva() {
      const porId = {};
      this.categorias.forEach((c) => {
        const padre = c.parent && porId[c.parent];
        porId[c.id] = c.linea || (padre !== undefined ? padre : null);
      });
      return porId;
    },
    productosPorLinea() {
      // Productos propios de cada categoría (total menos los de sus hijas visibles)
      const hijas = {};
      this.categorias.forEach((c) => {
        if (c.parent) hijas[c.parent] = (hijas[c.parent] || 0) + c.productos;
      });
      const totales = {};
      this.categorias.forEach((c) => {
        const propios = c.nivel === this.nivelBase + 1 ? c.productos : c.productos - (hijas[c.id] || 0);
        if (c.nivel > this.nivelBase + 1 || c.nivel < this.nivelBase) return;
        const linea = this.lineaEfectiva[c.id];
        const clave = linea || "sin";
        totales[clave] = (totales[clave] || 0) + propios;
      });
      return totales;
    },
    sinLinea() {
      return this.productosPorLinea.sin || 0;
    },
  },
  methods: {
    opcionesLinea(categoria) {
      const padre = this.categorias.find((c) => c.id === categoria.parent);
      const heredada = padre && this.lineaEfectiva[padre.id];
      const nombreHeredada = heredada && (this.lineas.find((l) => l.id === heredada) || {}).nombre;
      const vacia = nombreHeredada && categoria.nivel > this.nivelBase
        ? { id: null, nombre: `Como su categoría (${nombreHeredada})` }
        : SIN_LINEA;
      return [vacia, ...this.lineas];
    },
    avisar(text, color = "success") {
      this.$updateBus.$emit("showSnackBar", { show: true, button: false, color, text });
    },
    async fetch() {
      this.loading = true;
      try {
        const [lineas, categorias] = await Promise.all([
          ContabilidadApi.indexLineas(),
          ContabilidadApi.indexLineasCategorias(),
        ]);
        this.lineas = lineas.data;
        this.categorias = categorias.data;
      } catch (error) {
        this.avisar(getApiErrorMessage(error, "No se pudieron traer las líneas"), "error");
      } finally {
        this.loading = false;
      }
    },
    async asignar(categoria, lineaId) {
      this.guardando = categoria.id;
      try {
        const { data } = await ContabilidadApi.asignarLinea(categoria.id, lineaId);
        categoria.linea = data.linea;
        this.avisar(
          data.productos_movidos
            ? `${categoria.nombre}: ${data.productos_movidos} productos cambiaron de cuenta`
            : `${categoria.nombre} actualizada`
        );
      } catch (error) {
        this.avisar(getApiErrorMessage(error, "No se pudo asignar la línea"), "error");
      } finally {
        this.guardando = null;
      }
    },
    async crear() {
      if (!this.nombreNueva.trim()) return;
      this.creando = true;
      try {
        const { data } = await ContabilidadApi.createLinea({ nombre: this.nombreNueva.trim() });
        this.lineas.push(data);
        this.dialogNueva = false;
        this.nombreNueva = "";
        this.avisar(`Línea ${data.nombre} creada (cuenta ${data.cuenta_ventas})`);
      } catch (error) {
        const data = error.response && error.response.data;
        const mensaje = data && data.nombre ? data.nombre[0] : getApiErrorMessage(error, "No se pudo crear la línea");
        this.avisar(mensaje, "error");
      } finally {
        this.creando = false;
      }
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>
